<script lang="ts" setup>
import { Card } from '~/static/cards'
import { fabric } from '~/utils/editor/fabric'
import { EditorData } from '~/static/enums/editor-state'
import MDropdownMenu from '~/components/molecules/DropdownMenu.vue'
import RModalDownloadNotAvailable from '~/components/organs/modals/DownloadNotAvailable.vue'
import { Dropdown as FDropdown } from 'floating-vue'
import { initBackgroundFromJson, initObjectsFromJson } from '~/utils/editor/helpers/ExportHelper'
import { calculateZoom } from '~/utils/editor/helpers/Helpers'
import { useEditorStore } from '~/store/editor'
import { storeToRefs } from 'pinia'
import { openModal } from '~/utils/Modal'
import { useAuth } from '~/store/auth'
import { ObjectMode } from '~/static/enums/object-type'
import { downloadCard } from '~/utils/editor/helpers/DownloadHelper'

interface Props {
  card: Card
  height: number
  width: number
  inDirectory: boolean
  isCard: boolean
  onlyImage?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'open'): void
  (e: 'change-name'): void
  (e: 'clone'): void
  (e: 'put-to-directory'): void
  (e: 'exclude-from-directory'): void
  (e: 'delete'): void
}>()

const canvasRef: Ref = ref(null)
let canvas = new fabric.StaticCanvas(undefined)
let scalefactor = 1
const containerWidth = ref(props.width)
const containerHeight = ref(props.height)

const containerBorderRadius = ref('')
onMounted(async () => {
  if (canvasRef.value !== null && props.card.preview === null && props.card.data !== null) {
    const data: EditorData = JSON.parse(props.card.data)

    // Setting background
    const background = initBackgroundFromJson(data.background)

    canvas = new fabric.StaticCanvas(canvasRef.value, {
      width: background.width,
      height: background.height,
    })
    canvas.add(background.object)

    // props.width х props.height - максимальный размер рабочей области
    scalefactor = calculateZoom(background.width, background.height, props.width, props.height)

    const { objects: generatedObjects } = initObjectsFromJson(data.objects)
    for (const object of generatedObjects) {
      try {
        await object.load(ObjectMode.Editing)
        await object.draw()
        canvas.add(object.object)
      } catch (e) {
        console.log(e)
      }
    }

    canvas.renderAll()
    canvas.setZoom(scalefactor)

    containerWidth.value = Math.round(background.width * scalefactor)
    containerHeight.value = Math.round(background.height * scalefactor)

    if (background.borderRadius > 0) {
      const realBorderWidth = background.borderExists ? background.border : 0
      const borderRadiusWithBorderWidth = background.borderRadius + realBorderWidth / 2
      const backgroundWidthRadius = borderRadiusWithBorderWidth / background.width * 100
      const backgroundHeightRadius = borderRadiusWithBorderWidth / background.height * 100
      containerBorderRadius.value =
        `${Math.min(backgroundWidthRadius, 50)}% / ${Math.min(backgroundHeightRadius, 50)}%`
    }
  }
})

watch(() => {
  return {
    width: props.width,
    height: props.height,
  }
}, () => {
  const width = canvas.getWidth()
  const height = canvas.getHeight()
  // props.width х props.height - максимальный размер рабочей области
  const widthScaleFactor = (props.width - 2) / width
  const heightScaleFactor = (props.height - 25) / height
  scalefactor = widthScaleFactor > heightScaleFactor ? heightScaleFactor : widthScaleFactor
  containerWidth.value = Math.round(width * scalefactor)
  containerHeight.value = Math.round(height * scalefactor)
  canvas.setZoom(scalefactor)
})

const openCard = () => {
  emit('open')
}

const editorStore = useEditorStore()

const authStore = useAuth()
const { user } = storeToRefs(authStore)

const loading = ref(false)

const download = () => {
  if (user.value && !user.value.subscription && user.value.remaining_downloads === 0) {
    openModal(RModalDownloadNotAvailable)
    return
  }

  loading.value = true
  authStore.markDownload().then(() => {
    editorStore.loadData(props.card.id)
      .then(async (data) => {
        downloadCard(data, props.card.name).finally(() => {
          loading.value = false
        })
      }).catch(() => {
        loading.value = false
      })
  }).catch(() => {
    loading.value = false
  })
}

const cardMenu = computed(() => {
  const items: { text: string, handler: () => void, colorRed?: boolean }[] = [
    {
      text: 'Скачать',
      handler: download,
    },
    {
      text: 'Переименовать',
      handler: () => {
        emit('change-name')
      },
    },
    {
      text: 'Сделать копию',
      handler: () => {
        emit('clone')
      },
    },
    {
      text: 'Переместить в папку',
      handler: () => {
        emit('put-to-directory')
      },
    },
  ]
  if (props.inDirectory) {
    items.push({
      text: 'Исключить из папки',
      handler: () => {
        emit('exclude-from-directory')
      },
    })
  }
  items.push({
    text: 'Удалить',
    handler: () => {
      emit('delete')
    },
    colorRed: true,
  })
  return items
})

</script>

<template>
  <div>
    <div class="template">
      <div
        v-if="card.preview === null && card.data !== null"
        class="img"
        @click="openCard"
      >
        <div
          :style="{
            width: containerWidth + 'px',
            height: containerHeight + 'px',
            borderRadius: containerBorderRadius,
          }"
        >
          <canvas ref="canvasRef" />
        </div>
      </div>
      <div
        v-else
        class="template__wr-img"
      >
        <NuxtImg
          format="png"
          :src="card.preview ?? ''"
          :alt="card.name"
          @click="openCard"
        />
        <div
          v-if="loading"
          class="loading-card"
        >
          <div
            class="spinner-border"
            role="status"
          />
        </div>
      </div>
      <div
        v-if="!onlyImage"
        class="d-flex align-items-start justify-content-between mt-2"
      >
        <span
          class="me-2 text-break"
          @click="openCard"
        >
          {{ card.name }}
        </span>
        <FDropdown
          v-if="isCard"
          placement="bottom-start"
          :distance="4"
          :delay="0.3"
          :skidding="-12"
        >
          <button class="d-flex button-icon btn-yet">
            <BootstrapIconThreeDots class="flex-shrink-0 icon-size-20" />
          </button>
          <template #popper="{ hide }">
            <MDropdownMenu
              :items="cardMenu"
              theme="light-2"
              position="start"
              @click="hide"
            />
          </template>
        </FDropdown>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.template {
  cursor: pointer;

  & span {
    display: inline-block;
    transition: all .2s ease-in-out;

    @media (any-hover: hover) {
      &:hover {
        color: $indigo-500;
      }
    }
  }

  .btn-yet {
    color: $gray-900;
    margin: rem(2px) 0 0 0;

    @media (any-hover: hover) {
      &:hover {
        color: $indigo-500;
      }
    }
  }

  & .img, img {
    width: 100%;
    aspect-ratio: 166 / 220;
    border: 1px solid #c4ced1;
    border-radius: 4px;
    transition: all .2s ease-in-out;
    object-fit: contain;
    overflow: hidden;
    position: relative;
    background-color: #ebecf0;

    div {
      overflow: hidden;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translateY(-50%) translateX(-50%);
      background-image: linear-gradient(45deg, #b0b0b0 25%, transparent 25%),
      linear-gradient(-45deg, #b0b0b0 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, #b0b0b0 75%),
      linear-gradient(-45deg, transparent 75%, #b0b0b0 75%);
      background-size: 20px 20px;
      background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
    }
  }

  &__wr-img {
    position: relative;

    .loading-card {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border-radius: rem(4px);
      background: rgba(255, 255, 255, .5);
      display: flex;
      align-items: center;
      justify-content: center;

      .spinner-border {
        color: $indigo-500;
      }
    }
  }

  @media (any-hover: hover) {
    &:hover .img {
      border-color: $indigo-500;
    }
  }
}
</style>
